/**
 * The person's in-app notifications (collaboration phases 2 to 4): mentions in comments of shared projects, handoff
 * notes left for them, an editor's answer to their suggestion and notes AI agents or people left for them, kept by the
 * sandbox gateway (`GET /api/v1/me/inbox`, hooks/useInbox.js: one live poll shared with the Activity badge). Hidden
 * where the gateway has no inbox (404). Opening one marks it read and opens its project.
 * With them, the sandbox admin's messages (news, maintenance, incidents, information; hooks/useSandboxMessages.js):
 * a kind icon, the text with its links, the time; opening one marks it read. Both count in the unread number, and both
 * update live (utils/livePoll.js), without a reload.
 * And where the person's deliveries to Mia-Platform Console got to (hooks/useConsoleDeliveries.js): "Your app was
 * deployed to dev", "… is waiting for review in Console", "… could not be delivered" with why; opening one opens the
 * project in Flow, its link opens Console (or the app in dev).
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { BellIcon, RocketLaunchIcon } from '@phosphor-icons/react';
import { ago } from '../../utils/blameModel';
import { initialsOfName } from '../../utils/liveModel';
import { ParticipantMark } from '../presence/Presence';
import { ResponsivePopover } from './BottomSheet';
import useInbox from '../../hooks/useInbox';
import useSandboxMessages from '../../hooks/useSandboxMessages';
import { refreshConsoleDeliveries, useConsoleNotices } from '../../hooks/useConsoleDeliveries';
import { messageHeadline } from '../../utils/sandboxMessages';
import MessageIcon from '../messages/MessageIcon';
import MessageText from '../messages/MessageText';
import './Collab.css';
import '../messages/SandboxMessages.css';

/** "Sara mentioned you on StoreMap.jsx:7". */
export function notificationText(item) {
  const who = String(item?.by?.name || item?.by?.email || 'Someone').split(/[\s@]/)[0];
  if (item?.kind === 'mention') {
    const where = item.comment?.path ? `${item.comment.path.split('/').pop()}${Number.isInteger(item.comment.line) ? `:${item.comment.line}` : ''}` : 'a comment';
    return `${who} mentioned you on ${where}`;
  }
  if (item?.kind === 'handoff') { return `${who} left you a note`; }
  if (item?.kind === 'share') { return `${who} shared ${item.project?.name || 'a project'} with you${item.share?.role ? ` as ${item.share.role}` : ''}`; }
  if (item?.kind === 'joined') { return `${String(item.joined?.name || who).split(/\s/)[0]} joined ${item.project?.name || 'your project'}`; }
  if (item?.kind === 'conflict') {
    const c = item.conflict || {};
    const file = c.path ? c.path.split('/').pop() : 'a file';
    return `${c.from || who} changed ${file} where you were editing · ${c.lines || 1} line${c.lines === 1 || !c.lines ? '' : 's'} overlap${c.lines === 1 || !c.lines ? 's' : ''} yours · Compare`;
  }
  if (item?.kind === 'agent-done') {
    const d = item.agentDone || {};
    return `${d.from || who} is done${(d.files || []).length ? `: ${d.files.length} file${d.files.length === 1 ? '' : 's'} changed` : ''}`;
  }
  if (item?.kind === 'note' && item.agentNote?.chat) {
    return `${item.agentNote.from || item.by?.name || 'Someone'} ${item.agentNote.chat === 'mention' ? 'mentioned you in Messages' : 'sent you a message'}`;
  }
  if (item?.kind === 'note') {
    const from = item.agentNote?.from || item.by?.name || 'Someone';
    const where = item.agentNote?.path ? ` on ${item.agentNote.path.split('/').pop()}${Number.isInteger(item.agentNote.line) ? `:${item.agentNote.line}` : ''}` : '';
    return `${from} left you a note${where}`;
  }
  if (item?.kind === 'suggestion') {
    const file = item.suggestion?.path ? String(item.suggestion.path).split('/').pop() : 'a file';
    const verb = item.suggestion?.status === 'accepted' ? 'accepted' : item.suggestion?.status === 'rejected' ? 'declined' : 'reviewed';
    return `${who} ${verb} your suggestion on ${file}`;
  }
  return `${who} · ${item?.kind || 'notification'}`;
}

/**
 * Where a notification opens: the commented line (mention), the suggested file, the last file a handoff's writer changed;
 * else the project (`/open/:projectId?file=&line=`, see pages/OpenProject.jsx).
 */
export function notificationLink(item) {
  const base = `/open/${encodeURIComponent(item.projectId)}`;
  const file = item.kind === 'mention' ? item.comment?.path
    : item.kind === 'suggestion' ? item.suggestion?.path
      : item.kind === 'handoff' ? (item.note?.files || [])[0]
        : item.kind === 'note' ? item.agentNote?.path
          : item.kind === 'conflict' ? item.conflict?.path
            : item.kind === 'agent-done' ? (item.agentDone?.files || [])[0] : null;
  if (!file) { return base; }
  const lineOf = item.kind === 'mention' ? item.comment?.line : item.kind === 'note' ? item.agentNote?.line : item.kind === 'conflict' ? item.conflict?.line : null;
  const line = Number.isInteger(lineOf) ? `&line=${lineOf}` : '';
  return `${base}?file=${encodeURIComponent(file)}${line}`;
}

/** The quoted line under a notification: the comment, the note, or the reason a suggestion was declined. */
export function notificationExcerpt(item) {
  if (item?.comment?.excerpt) { return item.comment.excerpt; }
  if (item?.note?.excerpt) { return item.note.excerpt; }
  if (item?.agentNote?.excerpt) { return item.agentNote.excerpt; }
  if (item?.suggestion?.reason) { return item.suggestion.reason; }
  return null;
}

export default function NotificationsBell({ onNavigate, collapsed = false }) {
  const [open, setOpen] = useState(false);
  const { inbox, refresh: refreshInbox, read: readInbox } = useInbox();
  const { messages, refresh: refreshMessages, read: readMessages } = useSandboxMessages();
  const { notices: deliveries, unread: deliveriesUnread, read: readDeliveries } = useConsoleNotices();

  if (!inbox && !messages && deliveries.length === 0) { return null; }
  const items = inbox?.items || [];
  const notices = messages?.items || [];
  const unread = (inbox ? inbox.unread ?? items.filter((i) => !i.read).length : 0) + (messages?.unread || 0) + deliveriesUnread;
  // One list, newest first: the admin's messages (by their last change) and the deliveries among the collaboration notifications.
  const rows = [
    ...notices.map((m) => ({ type: 'message', at: m.updatedAt || m.at, item: m })),
    ...items.map((i) => ({ type: 'inbox', at: i.at, item: i })),
    ...deliveries.map((d) => ({ type: 'delivery', at: d.at, item: d })),
  ].sort((a, b) => String(b.at || '').localeCompare(String(a.at || '')));

  const openItem = async (item) => {
    setOpen(false);
    if (!item.read) { readInbox([item.id]); }
    if (item.projectId && onNavigate) { onNavigate(notificationLink(item)); }
  };

  const markAll = async () => {
    if (inbox && inbox.unread !== 0) { readInbox(null); }
    if (messages?.unread) { readMessages(null); }
    if (deliveriesUnread) { readDeliveries(null); }
  };

  const deliveryRow = (d) => (
    <div className={`collab-inbox-item${d.read ? '' : ' is-unread'}`} data-testid="inbox-delivery" key={`delivery:${d.id}`}>
      <button
        className="collab-inbox-delivery-main"
        onClick={() => { setOpen(false); if (!d.read) { readDeliveries([d.id]); } if (onNavigate) { onNavigate(`/open/${encodeURIComponent(d.projectId)}`); } }}
        type="button"
      >
        <span aria-hidden className={`collab-inbox-delivery-mark is-${d.state}`}><RocketLaunchIcon size={16} /></span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span>{d.text}</span>
          {d.detail && <span className="collab-muted" style={{ display: 'block', fontWeight: 400 }}>{d.detail}</span>}
          <span className="collab-muted collab-small" style={{ display: 'block', fontWeight: 400 }}>{d.title}{d.version ? ` ${d.version}` : ''} · Mia-Platform Console · {ago(d.at)}</span>
        </span>
      </button>
      {d.link && (
        <a className="collab-link collab-small" href={d.link} onClick={() => { if (!d.read) { readDeliveries([d.id]); } }} rel="noopener noreferrer" target="_blank">
          {d.linkLabel || 'Open in Console'}
        </a>
      )}
    </div>
  );

  const messageRow = (m) => {
    const tone = m.kind === 'incident' && m.status === 'resolved' ? 'resolved' : m.kind;
    return (
      <div className={`sandbox-msg-item${m.read ? '' : ' is-unread'}`} data-testid="inbox-message" key={`msg:${m.id}`}>
        <button className="sandbox-msg-item-main" onClick={() => { if (!m.read) { readMessages([m.id]); } }} type="button">
          <span className={`sandbox-msg-item-mark is-${tone}`}><MessageIcon kind={m.kind} size={16} status={m.status} /></span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span className="sandbox-msg-item-title">{messageHeadline(m)}</span>
            <span className="collab-muted collab-small" style={{ display: 'block', fontWeight: 400 }}>
              From the Flow team · {m.version > 1 ? `updated ${ago(m.updatedAt)}` : ago(m.at)}{m.read ? '' : ' · new'}
            </span>
          </span>
        </button>
        {m.body && <MessageText className="sandbox-msg-item-body" text={m.body} />}
        {m.link && <a className="sandbox-msg-item-link" href={m.link} onClick={() => { if (!m.read) { readMessages([m.id]); } }} rel="noopener noreferrer" target="_blank">Details</a>}
      </div>
    );
  };

  const content = (
    <div className="collab-inbox" data-testid="inbox">
      <div style={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between', paddingBottom: 6 }}>
        <strong className="collab-inbox-title">Notifications{unread ? ` · ${unread} new` : ''}</strong>
        {unread > 0 && <button className="collab-link collab-small" onClick={markAll} type="button">Mark all read</button>}
      </div>
      {rows.length === 0 && <div className="collab-muted collab-small">Nothing yet. Mentions, notes for you and answers to your suggestions show here.</div>}
      {rows.map(({ type, item }) => (type === 'message' ? messageRow(item) : type === 'delivery' ? deliveryRow(item) : (
        <button className={`collab-inbox-item${item.read ? '' : ' is-unread'}`} key={item.id} onClick={() => openItem(item)} type="button">
          {(item.kind === 'note' && item.agentNote?.kind === 'agent') || item.kind === 'agent-done' || item.kind === 'conflict'
            ? <ParticipantMark agent initials="AI" size={26} slot={3} />
            : <ParticipantMark initials={initialsOfName(item.by?.name || item.by?.email)} size={26} slot={1} />}
          <span style={{ flex: 1, minWidth: 0 }}>
            <span>{notificationText(item)}</span>
            {notificationExcerpt(item) && <span className="collab-muted" style={{ display: 'block', fontWeight: 400 }}>“{notificationExcerpt(item)}”</span>}
            <span className="collab-muted collab-small" style={{ display: 'block', fontWeight: 400 }}>{item.project?.name || item.projectId} · {ago(item.at)}</span>
          </span>
        </button>
      )))}
    </div>
  );

  return (
    // A popover; on a phone, a full-width sheet from the bottom.
    <ResponsivePopover content={content} onOpenChange={(next) => { setOpen(next); if (next) { refreshInbox(); refreshMessages(); refreshConsoleDeliveries(); } }} open={open} placement={collapsed ? 'rightBottom' : 'topLeft'} testId="inbox-sheet" title={unread ? `Notifications · ${unread} new` : 'Notifications'}>
      <button
        aria-label={unread ? `Notifications, ${unread} new` : 'Notifications'}
        className="flow-sidebar-settings collab-bell"
        data-testid="notifications-bell"
        title="Notifications"
        type="button"
      >
        <BellIcon size={20} />
        {unread > 0 && <span className="collab-bell-count">{unread > 99 ? '99+' : unread}</span>}
      </button>
    </ResponsivePopover>
  );
}

NotificationsBell.propTypes = {
  collapsed: PropTypes.bool,
  onNavigate: PropTypes.func,
};
